import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

// In-memory cache so re-visiting a route is instant (and we don't re-fetch on
// every render). Keyed by pathname.
const cache = new Map();

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${CSS.escape(key)}"]`);
  if (content === undefined || content === null || content === "") {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(obj) {
  const id = "seo-jsonld";
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!obj) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.setAttribute("data-seo", "1");
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
}

function apply(seo) {
  if (!seo) return;
  if (seo.title) document.title = seo.title;
  upsertMeta("name", "description", seo.description);
  upsertMeta("name", "keywords", seo.keywords);
  upsertMeta("name", "robots", seo.robots);
  upsertLink("canonical", seo.canonical);
  const og = seo.og || {};
  upsertMeta("property", "og:title", og.title);
  upsertMeta("property", "og:description", og.description);
  upsertMeta("property", "og:type", og.type);
  upsertMeta("property", "og:url", og.url);
  upsertMeta("property", "og:site_name", og.site_name);
  upsertMeta("property", "og:image", og.image);
  const tw = seo.twitter || {};
  upsertMeta("name", "twitter:card", tw.card);
  upsertMeta("name", "twitter:site", tw.site);
  upsertMeta("name", "twitter:title", tw.title);
  upsertMeta("name", "twitter:description", tw.description);
  upsertMeta("name", "twitter:image", tw.image);
  setJsonLd(seo.json_ld);
}

/**
 * Drives the document <head> for every public route from the server-resolved
 * SEO config (static pages + dynamic entity pages). Mounted once in
 * PublicLayout, it reacts to pathname changes.
 */
export function Seo() {
  const { pathname } = useLocation();
  useEffect(() => {
    let active = true;
    if (cache.has(pathname)) apply(cache.get(pathname));
    api
      .get("/seo/resolve", { params: { path: pathname } })
      .then(({ data }) => {
        cache.set(pathname, data);
        if (active) apply(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pathname]);
  return null;
}

export default Seo;
